import { Outlet, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import OceanAtmosphere from '@/components/atmosphere/OceanAtmosphere'
import AmbientSound from '@/components/atmosphere/AmbientSound'
import Icon from '@/components/ui/Icon'
import { IcBuilding2, IcCamera, IcCompass, IcGlasses, IcGraduationCap, IcHandshake, IcHospital, IcMedal, IcMicroscope, IcPlane, IcWaves, IcWind } from '@/components/ui/icons'

const FEATURES = [
  { icon: IcGlasses, label: 'Subacqueo',              desc: 'Logbook, immersioni, certificazioni' },
  { icon: IcWind, label: 'Apneista',               desc: 'Profilo freediving, sessioni' },
  { icon: IcCompass, label: 'Divemaster',             desc: 'Gestione gruppi, guide, escursioni' },
  { icon: IcMedal, label: 'Istruttore',             desc: 'Corsi, studenti, progress tracking' },
  { icon: IcHospital, label: 'Medico Subacqueo',       desc: 'Idoneità, cartelle cliniche, AI' },
  { icon: IcBuilding2, label: 'Centro Diving',          desc: 'Booking, staff, attrezzatura, HR' },
  { icon: IcPlane, label: 'Agenzia Viaggi',         desc: 'Pacchetti, itinerari, clienti' },
  { icon: IcCamera, label: 'Fotografo Subacqueo',    desc: 'Portfolio, media library, vendite' },
  { icon: IcMicroscope, label: 'Centro di Ricerca',      desc: 'Marine biology, specie, field journal' },
  { icon: IcHandshake, label: 'Associazione',           desc: 'Soci, eventi, comunicazioni, shop' },
  { icon: IcGraduationCap, label: 'Accademia',              desc: 'Corsi di formazione, certificazioni' },
  { icon: IcWaves, label: 'Pianificazione',         desc: 'Meteo marino, siti, condizioni' },
]

export default function AuthLayout() {
  const { t, i18n } = useTranslation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  if (isAuthenticated) return <Navigate to="/app/dashboard" replace />

  return (
    <div className="min-h-screen bg-ocean-gradient flex flex-col lg:flex-row">
      {/* Left panel — branding */}
      <div className="relative lg:w-[52%] flex flex-col text-white lg:min-h-screen overflow-hidden">
        {/* Atmosfera marina viva dietro al branding (Canvas, reduced-motion aware) */}
        <OceanAtmosphere rayStrength={0.45} />

        {/* Suono del mare — opzionale, off di default */}
        <div className="absolute top-4 right-4 z-20">
          <AmbientSound />
        </div>

        {/* Header with logo */}
        <div className="relative z-10 flex flex-col items-center pt-10 pb-6 px-8">
          <img
            src="/brand/main_logo.png"
            alt={t('auto.layoutsAuthLayout.ediveLogo')}
            className="w-28 h-28 lg:w-36 lg:h-36 object-contain drop-shadow-2xl mb-5"
          />
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight" style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}>
            eDive<span className="text-cyan-400">{t('auto.layoutsAuthLayout.tech')}</span>
          </h1>
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-white/40 mt-1 mb-3">
            {t('auto.layoutsAuthLayout.technologyForTheOcean')}
          </p>
          <p className="text-ocean-200 text-sm text-center max-w-xs leading-relaxed">
            {t('auth.tagline')}
          </p>
        </div>

        {/* Roles & features grid */}
        <div className="relative z-10 flex-1 px-6 lg:px-10 pb-6">
          <p className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-4 text-center lg:text-left">
            {t('auto.layoutsAuthLayout.chiUsaEdiveTech')}
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {FEATURES.map(({ icon, label, desc }) => (
              <div
                key={label}
                className="flex items-start gap-2 bg-white/5 hover:bg-white/10 transition-colors rounded-xl px-3 py-2.5"
              >
                <span className="text-base shrink-0 mt-0.5"><Icon icon={icon} size="sm" /></span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white leading-tight">{label}</p>
                  <p className="text-[11px] text-white/50 leading-snug mt-0.5 truncate">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom banner */}
        <div className="relative z-10 mt-auto">
          <img
            src="/brand/testata.jpg"
            alt={t('auto.layoutsAuthLayout.ediveBanner')}
            className="w-full object-cover max-h-28 lg:max-h-36"
          />
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 bg-white lg:min-h-screen">
        <div className="w-full max-w-md">
          {/* Mobile-only mini logo */}
          <div className="flex lg:hidden items-center justify-center gap-3 mb-6">
            <img src="/brand/main_logo.png" alt="eDive" className="w-10 h-10 object-contain" />
            <span className="text-xl font-black text-ocean-800" style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}>
              eDive<span className="text-cyan-500">{t('auto.layoutsAuthLayout.tech')}</span>
            </span>
          </div>

          <Outlet />

          <div className="mt-8 flex items-center justify-between text-xs text-gray-400">
            <span>© {new Date().getFullYear()} eDive.tech</span>
            <button
              onClick={() => {
                const next = i18n.language === 'it' ? 'en' : 'it'
                i18n.changeLanguage(next)
                localStorage.setItem('edive-lang', next)
              }}
              className="hover:text-gray-600 transition-colors"
            >
              {i18n.language === 'it' ? 'English' : 'Italiano'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
